import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import {
    Admin,
    AdminWithPermissions,
    AdminWithRoles,
    GroupedPermissions
} from '../types/admin.types';
import { AdminAuthService } from '../service/supabase/AdminAuthService';

interface AdminContextType {
    admin: Admin | null;
    roles: AdminWithRoles['roles'];
    permissions: AdminWithPermissions[];
    groupedPermissions: GroupedPermissions;
    isAuthenticated: boolean;
    isLoading: boolean;
    login: (credentials: { user_name: string; pass_word: string }) => Promise<boolean>;
    logout: () => void;
    checkPermission: (module: string, subModule: string | null, type: 'view' | 'add' | 'edit' | 'delete') => boolean;
    hasModuleAccess: (module: string) => boolean;
    hasRole: (roleName: string) => boolean;
    getRolePermissions: (roleName: string) => AdminWithPermissions[];
    refreshPermissions: () => Promise<void>;
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

interface AdminProviderProps {
    children: ReactNode;
}

export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
    const [admin, setAdmin] = useState<Admin | null>(null);
    const [roles, setRoles] = useState<AdminWithRoles['roles']>([]);
    const [permissions, setPermissions] = useState<AdminWithPermissions[]>([]);
    const [groupedPermissions, setGroupedPermissions] = useState<GroupedPermissions>({});
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Kiểm tra admin đã đăng nhập khi component mount
    useEffect(() => {
        checkExistingSession();
    }, []);

    const checkExistingSession = async () => {
        try {
            setIsLoading(true);
            const stored = AdminAuthService.getStoredAdminInfo();

            if (stored.token && stored.admin && stored.permissions) {
                // Verify token và refresh thông tin
                const result = await AdminAuthService.getCurrentAdmin(stored.token);

                if (result.success && result.data) {
                    setAdmin(result.data.admin);
                    setRoles(result.data.roles);
                    setPermissions(result.data.permissions);
                    setGroupedPermissions(AdminAuthService.groupPermissions(result.data.permissions));
                    setIsAuthenticated(true);
                } else {
                    // Token hết hạn hoặc không hợp lệ
                    AdminAuthService.logout();
                    setAdmin(null);
                    setRoles([]);
                    setPermissions([]);
                    setGroupedPermissions({});
                    setIsAuthenticated(false);
                }
            }
        } catch (error) {
            console.error('Error checking admin session:', error);
            AdminAuthService.logout();
            setAdmin(null);
            setRoles([]);
            setPermissions([]);
            setGroupedPermissions({});
            setIsAuthenticated(false);
        } finally {
            setIsLoading(false);
        }
    };

    const login = async (credentials: { user_name: string; pass_word: string }): Promise<boolean> => {
        try {
            setIsLoading(true);
            const result = await AdminAuthService.loginAdmin(credentials);

            if (result.success && result.data) {
                // Lưu session
                AdminAuthService.saveAdminSession(result.data);

                // Cập nhật state
                setAdmin(result.data.admin);
                setRoles(result.data.roles);
                setPermissions(result.data.permissions);
                setGroupedPermissions(AdminAuthService.groupPermissions(result.data.permissions));
                setIsAuthenticated(true);

                return true;
            } else {
                throw new Error(result.error || 'Đăng nhập thất bại');
            }
        } catch (error) {
            console.error('Login error:', error);
            return false;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = () => {
        AdminAuthService.logout();
        setAdmin(null);
        setRoles([]);
        setPermissions([]);
        setGroupedPermissions({});
        setIsAuthenticated(false);
    };

    const checkPermission = (
        module: string,
        subModule: string | null,
        type: 'view' | 'add' | 'edit' | 'delete'
    ): boolean => {
        return AdminAuthService.checkPermission(permissions, module, subModule, type);
    };

    const hasModuleAccess = (module: string): boolean => {
        return AdminAuthService.hasModuleAccess(permissions, module);
    };

    const hasRole = (roleName: string): boolean => {
        return roles.some(role => role.name === roleName);
    };

    const getRolePermissions = (roleName: string): AdminWithPermissions[] => {
        return permissions.filter(permission => permission.role_name === roleName);
    };

    const refreshPermissions = async () => {
        if (!admin?.id) return;

        try {
            const result = await AdminAuthService.getAdminRolesAndPermissions(admin.id);
            if (result.success && result.data) {
                const newPermissions = AdminAuthService.convertRolesToPermissions(admin, result.data);

                setRoles(result.data);
                setPermissions(newPermissions);
                setGroupedPermissions(AdminAuthService.groupPermissions(newPermissions));

                // Cập nhật localStorage
                const stored = AdminAuthService.getStoredAdminInfo();
                if (stored.token) {
                    AdminAuthService.saveAdminSession({
                        admin,
                        roles: result.data,
                        permissions: newPermissions,
                        token: stored.token
                    });
                }
            }
        } catch (error) {
            console.error('Error refreshing permissions:', error);
        }
    };

    const contextValue: AdminContextType = {
        admin,
        roles,
        permissions,
        groupedPermissions,
        isAuthenticated,
        isLoading,
        login,
        logout,
        checkPermission,
        hasModuleAccess,
        hasRole,
        getRolePermissions,
        refreshPermissions
    };

    return (
        <AdminContext.Provider value={contextValue}>
            {children}
        </AdminContext.Provider>
    );
};

export const useAdmin = (): AdminContextType => {
    const context = useContext(AdminContext);
    if (context === undefined) {
        throw new Error('useAdmin must be used within an AdminProvider');
    }
    return context;
};
